import React, { useEffect } from 'react'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/Buttons/Button'
import { useTranslation } from '@/localization/useTranslation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons'
import { AuthErrorType } from '../../types'
import { STANDARD_AUTH_FORMS } from '@/constants/auth'
import { useRouter } from 'next/navigation'
import { clarity, CLARITY_EVENTS, CLARITY_TAGS } from '@/utils/clarity'
import { ErrorReferenceCode } from '@/components/ErrorReferenceCode'
import { RECAPTCHA_ERROR_CODES } from '@/types/recaptcha'
import { SSO_ERROR_CODES } from '@/actors/AuthActor/utils/toSsoAuthError'

import classes from '../authFeedbackModal.module.scss'

interface AuthErrorModalProps {
  error: AuthErrorType
  handleError: () => void
  handleRedirect?: (path: string) => void
  bodyByCode?: Partial<Record<string, string>>
}

export const AuthErrorModal: React.FC<AuthErrorModalProps> = (props) => {
  const { error, handleError, handleRedirect, bodyByCode } = props
  const router = useRouter()
  const { t } = useTranslation()

  const handleGoToHome = () => {
    router.push('/')
  }

  const handleTryAgain = () => {
    clarity.event(CLARITY_EVENTS.AUTH_RETRY_CLICKED)
    handleError()
  }

  const handleTryAgainFromMain = () => {
    clarity.event(CLARITY_EVENTS.AUTH_RETRY_CLICKED)
    handleRedirect?.(STANDARD_AUTH_FORMS.MAIN)
  }

  useEffect(() => {
    if (!error) return
    clarity.set(CLARITY_TAGS.AUTH_ERROR_CODE, String(error?.code ?? 'unknown'))
  }, [error])

  const ERROR_MESSAGES = {
    '4019': {
      body: 'Try reentering your login data, or click on “Recover password” if you have forgotten it.',
      buttons: [
        {
          text: 'Recover password',
          variant: 'tertiary',
          onClick: () => handleRedirect?.(STANDARD_AUTH_FORMS.RESET_PASSWORD)
        },
        {
          text: 'Try again',
          variant: 'primary',
          onClick: handleTryAgain
        }
      ]
    },
    '42215': {
      body: 'The email address you entered is not associated with an account.',
      buttons: [
        {
          text: 'Register',
          variant: 'tertiary',
          onClick: () => handleRedirect?.(STANDARD_AUTH_FORMS.REGISTER)
        },
        {
          text: 'Try again',
          variant: 'primary',
          onClick: handleTryAgain
        }
      ]
    },
    '4222': {
      body: 'You can login with this email or register with a different email.',
      buttons: [
        {
          text: 'Register',
          variant: 'tertiary',
          onClick: () => handleError()
        },
        {
          text: 'Log in',
          variant: 'primary',
          onClick: () => handleRedirect?.(STANDARD_AUTH_FORMS.LOGIN)
        },
      ]
    },
    '42242': {
      body: 'Your pair code is invalid or expired, please check and try again.',
      buttons: [
        {
          text: 'Try again',
          variant: 'primary',
          onClick: handleTryAgain
        }
      ]
    },
    '4011': {
      body: 'There was a problem validating your credentials.',
      buttons: [
        {
          text: 'Use another method',
          variant: 'tertiary',
          onClick: () => handleRedirect?.(STANDARD_AUTH_FORMS.LOGIN)
        },
        {
          text: 'Try again',
          variant: 'primary',
          onClick: handleTryAgain
        }
      ]
    },
    '5001': {
      body: 'Please try again later.',
      buttons: [
        {
          text: 'Try again',
          variant: 'primary',
          onClick: handleTryAgain
        }
      ]
    },
    // reCAPTCHA rejected the request (FZSPF-3835). Intentionally vague about
    // why — the server does not say whether it was a low score, a stale action
    // or an invalid token, so a bot can't tune against the response.
    '40301': {
      body: 'Please try again. If the problem persists, try using a different browser.',
      buttons: [
        {
          text: 'Try again',
          variant: 'primary',
          onClick: handleTryAgain
        }
      ]
    },
    '429': {
      body: 'Please try again later.',
      buttons: [
        {
          text: 'Close',
          variant: 'primary',
          onClick: () => handleError()
        }
      ]
    },
    // Federated sign-in (PRO-736). Both entries deliberately avoid Register:
    // the scenes that reach them are the sign-in-only ones (`denyRegister`,
    // e.g. /firetv), where creating an account is exactly what was refused, so
    // the email login is the only exit that can succeed.
    [SSO_ERROR_CODES.ACCOUNT_NOT_FOUND]: {
      body: 'Sign in with the email and password of your account to continue.',
      buttons: [
        {
          text: 'Close',
          variant: 'tertiary',
          onClick: handleTryAgain
        },
        {
          text: 'Continue with email',
          variant: 'primary',
          onClick: () => handleRedirect?.(STANDARD_AUTH_FORMS.LOGIN)
        }
      ]
    },
    [SSO_ERROR_CODES.FAILED]: {
      body: 'Please try again, or sign in with your email and password.',
      buttons: [
        {
          text: 'Continue with email',
          variant: 'tertiary',
          onClick: () => handleRedirect?.(STANDARD_AUTH_FORMS.LOGIN)
        },
        {
          text: 'Try again',
          variant: 'primary',
          onClick: handleTryAgain
        }
      ]
    },
    'NO_TOKEN': {
      body: 'No authentication token was found in the URL. Please try logging in again.',
      buttons: [
        {
          text: 'Go to home',
          variant: 'tertiary',
          onClick: handleGoToHome
        },
        {
          text: 'Try again',
          variant: 'primary',
          onClick: handleTryAgainFromMain
        }
      ]
    }
  }

  const RECAPTCHA_UNAVAILABLE_ENTRY = {
    body: 'Please refresh the page and try again. If the problem persists, check that your browser is not blocking Google reCAPTCHA.',
    buttons: [
      {
        text: 'Refresh',
        variant: 'primary',
        onClick: () => window.location.reload()
      }
    ]
  }

  const isRecaptchaUnavailable = !!error?.extra?.recaptchaUnavailable
  const codeKey = String(error?.code)
  const overrideBody = isRecaptchaUnavailable ? undefined : bodyByCode?.[codeKey]
  const errorEntry = isRecaptchaUnavailable
    ? RECAPTCHA_UNAVAILABLE_ENTRY
    : ERROR_MESSAGES[codeKey as keyof typeof ERROR_MESSAGES]
  const overrideButtons = [
    {
      text: 'Try again',
      variant: 'primary',
      onClick: handleTryAgain
    }
  ]

  // Only reCAPTCHA rejections show a reference code: their real cause is
  // deliberately withheld from the UI, so this is the user's only handle for
  // support. Other errors already say what went wrong.
  const errorCode = Number(error?.code)
  const referenceCode = RECAPTCHA_ERROR_CODES.includes(errorCode) ? errorCode : undefined

  const errorBody = t(overrideBody || errorEntry?.body || error?.extra?.info || 'Something went wrong')
  const errorButtons = errorEntry?.buttons || (overrideBody ? overrideButtons : [])

  return (
    <Modal isVisible={!!error} size='medium'>
      <div className={classes.content}>
        <FontAwesomeIcon
          size="4x"
          icon={faExclamationCircle}
          color="var(--error-300)"
        />
        <div className={classes.text}>
          <h3>{t(error?.userMessage || 'Something went wrong')}</h3>
          <p>{errorBody}</p>
          <ErrorReferenceCode code={referenceCode} />
        </div>
        <div className={classes.buttons}>
          {errorButtons?.length > 0 ?
            errorButtons?.map((button, index) => (
              <Button
                key={index}
                variant={button.variant as any}
                onClick={button.onClick}
                fullWidth
              >
                {t(button.text)}
              </Button>
            )) :
            <Button
              variant="primary"
              onClick={handleError}
              fullWidth
            >
              {t('Close')}
            </Button>
          }
        </div>
      </div>
    </Modal>
  )
}
